import { ClientLogger } from 'bernie-client';
import { getBexApiContext } from 'bernie-context';
import { QueryObject, SerializedData } from 'bernie-core';
import { Logger, NOOP_LOGGER } from 'bernie-logger';
import { ContextStore, Store } from 'bernie-plugin-mobx';
import { computed, observable } from 'mobx';
import {
  ActivityDateRangeInput,
  ActivityDestinationInput,
  ActivityDetailBasic,
  ActivityDetailOffer,
  ActivityDetailOffers,
  ActivityDetailReviews,
  ActivityDetailViewOptions,
  ActivityMarketingInfoInput,
  ActivityOfferFilterInput,
  ActivityReviewSort,
  ContextInput,
  PaginationInput
} from '../common/__generated__/api/types';
import { equals } from '../helpers/common-helper';
import { activityDateRangeInput } from '../helpers/controller-date-helper';
import { getIsoFormatDateFromDateString } from '../helpers/date-helper';
import { MarketingInfo } from '../helpers/marketing-cookie-helper';

const DEFAULT_COMMENTS_LENGTH = 10;
const COMMENTS_START_INDEX = 0;
const MCTC_MOD_ENABLED = 2;
// these values should only be set in controllers
// coming from the query
export interface DetailQueryObject extends QueryObject {
  readonly location: string;
  readonly startDate: string;
  readonly endDate: string;
  readonly swp: string;
  readonly rid: string;
}

export class DetailCriteriaStore extends Store {
  @observable public detailQuery: DetailQueryObject = {} as DetailQueryObject;

  @observable public offerFilters: ActivityOfferFilterInput = {};

  @observable public selectedDate: ActivityDetailBasic.Date | null = null;

  @observable public sortBy: ActivityReviewSort = ActivityReviewSort.NEWEST_TO_OLDEST;

  public offerId = '';

  public localDateFormat = 'yyyy-MM-dd';

  public activityId = '';

  private commentsIndex = COMMENTS_START_INDEX;

  private context: ContextInput = {} as ContextInput;

  private dateRange: ActivityDateRangeInput | null = null;

  private defaultCommentsLength = DEFAULT_COMMENTS_LENGTH;

  private marketingInfo?: MarketingInfo;

  private queryStringUpdated = true;

  private recommendedActivity = false;

  private reviewSource?: string | null;

  public constructor(
    state: SerializedData = {},
    logger: Logger | ClientLogger = NOOP_LOGGER,
    marketingInfo?: MarketingInfo
  ) {
    super(state, logger);
    if (marketingInfo) {
      this.marketingInfo = marketingInfo;
    }
  }

  public hydrate(data: SerializedData): void {
    this.detailQuery = { ...data.detailQuery };
    this.context = { ...data.context };
    this.activityId = data.activityId;
    this.selectedDate = data.selectedDate;
    this.queryStringUpdated = data.queryStringUpdated;
    this.localDateFormat = data.localDateFormat;
    this.offerFilters = data.offerFilters;
    this.marketingInfo = data.marketingInfo;
    delete data.marketingInfo;
  }

  public isQueryStringUpdated(): boolean {
    return this.queryStringUpdated;
  }

  public setQueryStringUpdated(queryStringUpdated: boolean): void {
    this.queryStringUpdated = queryStringUpdated;
  }

  public isRecommendedActivity(): boolean {
    return this.recommendedActivity;
  }

  public setRecommendedActivity(recommendedActivity: boolean): void {
    this.recommendedActivity = recommendedActivity;
  }

  public resetReviewsPaginationIndex(): void {
    this.commentsIndex = 0;
  }

  public setReviewSource(source?: string | null): void {
    this.reviewSource = source;
  }

  public resetDetailCriteriaStore(): void {
    this.detailQuery = {} as DetailQueryObject;
    this.resetReviewsPaginationIndex();
    this.activityId = '';
    this.sortBy = ActivityReviewSort.NEWEST_TO_OLDEST;
    this.selectedDate = null;
    this.queryStringUpdated = true;
  }

  public showMoreReviews(): void {
    this.commentsIndex += 1;
  }

  public setContext(context: ContextStore): void {
    this.context = getBexApiContext(context);
  }

  public changedQueryKeys(query: QueryObject, activityId: string): string[] {
    const detailKeys = ['location', 'endDate', 'startDate', 'rid', 'swp'];
    const changedKeys = [];
    detailKeys.forEach((key) => {
      if (this.isQueryKeyDifferent(query, key)) {
        changedKeys.push(key);
      }
    });

    if (!equals(activityId, this.activityId)) {
      changedKeys.push('activityId');
    }

    return changedKeys;
  }

  @computed
  public get detailVariables(): ActivityDetailBasic.Variables {
    return {
      context: this.context as ContextInput,
      destination: this.activityDestinationInput,
      dateRange: this.activityDateRangeInput,
      activityId: this.activityId,
      marketing: this.marketing,
      detailsViewOptions: this.detailsViewOptions
    };
  }

  @computed
  public get reviewsVariables(): ActivityDetailReviews.Variables {
    return {
      context: this.context as ContextInput,
      destination: this.activityDestinationInput,
      dateRange: this.activityDateRangeInput,
      activityId: this.activityId,
      pagination: this.reviewsPagination,
      sortBy: this.sortBy,
      source: this.reviewSource
    };
  }

  @computed
  public get allOffersVariables(): ActivityDetailOffers.Variables {
    const variables = {
      context: this.context as ContextInput,
      destination: this.activityDestinationInput,
      activityId: this.activityId,
      dateRange: this.activityDateRangeInput,
      marketing: this.marketing,
      detailsViewOptions: this.detailsViewOptions,
      filter: this.offerFilters,
      offerFilters: this.offerFilters
    };

    if (this.selectedDate) {
      variables.dateRange = {
        startDate: {
          day: this.selectedDate.day,
          month: this.selectedDate.month,
          year: this.selectedDate.year
        },
        endDate: {
          day: this.selectedDate.day,
          month: this.selectedDate.month,
          year: this.selectedDate.year
        }
      };
    }

    return variables;
  }

  @computed
  public get offerVariables(): ActivityDetailOffer.Variables {
    const variables = {
      context: this.context as ContextInput,
      destination: this.activityDestinationInput,
      activityId: this.activityId,
      dateRange: this.dateRange,
      marketing: this.marketing,
      detailsViewOptions: this.detailsViewOptions,
      offerId: this.offerId,
      filter: this.offerFilters
    };

    if (this.selectedDate) {
      variables.dateRange = {
        startDate: {
          day: this.selectedDate.day,
          month: this.selectedDate.month,
          year: this.selectedDate.year
        },
        endDate: {
          day: this.selectedDate.day,
          month: this.selectedDate.month,
          year: this.selectedDate.year
        }
      };
    }

    return variables;
  }

  public setActivityId(id: string): void {
    this.activityId = id;
  }

  public getActivityId(): string {
    return this.activityId;
  }

  private get reviewsPagination(): PaginationInput {
    const pagination: PaginationInput = {};
    pagination.size = this.defaultCommentsLength;
    pagination.startingIndex = this.commentsIndex;

    return pagination;
  }

  private isQueryKeyDifferent(query: QueryObject, key: string): boolean {
    if (key === 'startDate' || key === 'endDate') {
      if (query[key] !== '' && this.detailQuery[`${key}`] !== '') {
        return !equals(
          getIsoFormatDateFromDateString(query[key], this.localDateFormat),
          getIsoFormatDateFromDateString(this.detailQuery[`${key}`], this.localDateFormat)
        );
      }

      return true;
    }

    return !equals(query[key], this.detailQuery[key]);
  }

  private get activityDestinationInput(): ActivityDestinationInput {
    return { regionName: this.detailQuery.location, regionId: this.detailQuery.rid };
  }

  private get marketing(): ActivityMarketingInfoInput {
    const marketingInfo: ActivityMarketingInfoInput = {};

    if (this.marketingInfo) {
      marketingInfo.sem = this.marketingInfo.sem;
      marketingInfo.semdtl = this.marketingInfo.semdtl;
    }

    if (this.lxModEnabled) {
      marketingInfo.mctc = MCTC_MOD_ENABLED;
    }

    return marketingInfo;
  }

  private get lxModEnabled(): boolean {
    const mctcIsSet = this.detailQuery.mctc ? this.detailQuery.mctc === MCTC_MOD_ENABLED.toString() : false;

    return mctcIsSet || (typeof document !== 'undefined' && document.cookie.includes('lx_mod_enabled=true'));
  }

  private get activityDateRangeInput(): ActivityDateRangeInput | null {
    let dateRange = activityDateRangeInput(this.detailQuery, this.localDateFormat);

    if (!dateRange && this.selectedDate) {
      const bexSelectedDate = {
        day: this.selectedDate.day,
        month: this.selectedDate.month,
        year: this.selectedDate.year
      };

      dateRange = {
        startDate: bexSelectedDate,
        endDate: bexSelectedDate
      };
    }

    return dateRange;
  }

  private get detailsViewOptions(): ActivityDetailViewOptions[] | null {
    const detailsViewOptions: ActivityDetailViewOptions[] = [];

    if (this.detailQuery.swp !== 'off') {
      detailsViewOptions.push(ActivityDetailViewOptions.SHOP_WITH_POINTS);
    }

    return detailsViewOptions.length > 0 ? detailsViewOptions : null;
  }
}
